import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Alert } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.css";
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore'; // Add onSnapshot for real-time updates
import { db } from '../../base'; // Update the path to your Firebase config
import "../userAccount/accountModal.css"
import { Link } from 'react-router-dom';
import AdminDashboardModal from '../admin/AdminDashboardModal';
import { createCustomerPortalSession, cancelSubscription, createCheckoutSession } from '../../services/stripeService';
import { pricingPlans } from '../../plans/pricingPlans'; // Import pricing plans for accurate price display
import { toast } from 'react-toastify';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useProjectLimits } from '../../hooks/useProjectLimits';

const EXTRA_GENERATION_CREDITS_PRICE_ID = 'price_1SAXsDLs3VmGBJkcN3YPlb1P';
const EXTRA_GENERATION_CREDITS_PRODUCT_ID = 'prod_T6lPqA2icpJwlv';
const EXTRA_GENERATION_CREDITS_PER_PURCHASE = 20;

const AccountModal = ({ isOpen, onRequestClose, currentUser, handleLogOut, userRole }) => {
    const { t, i18n } = useTranslation();
    const { restartOnboarding } = useOnboarding();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showAdminDashboard, setShowAdminDashboard] = useState(false);
    const [isProcessingCancellation, setIsProcessingCancellation] = useState(false);
    const [isBuyingCredits, setIsBuyingCredits] = useState(false);
    const [showBottomScrollHint, setShowBottomScrollHint] = useState(false);
    const [showTopScrollHint, setShowTopScrollHint] = useState(false);
    const accountModalBodyRef = useRef(null);
    const { projectCount } = useProjectLimits(currentUser, userData);

    const updateScrollIndicators = () => {
        const el = accountModalBodyRef.current;
        if (!el) return;

        const hasOverflow = el.scrollHeight > el.clientHeight + 2;
        const atTop = el.scrollTop <= 2;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;

        setShowTopScrollHint(hasOverflow && !atTop);
        setShowBottomScrollHint(hasOverflow && !atBottom);
    };

    useEffect(() => {
        if (!currentUser || !isOpen) {
            setUserData(null);
            return;
        }

        setLoading(true);
        setError('');
        
        const userDocRef = doc(db, 'users', currentUser.uid);

        // Set up real-time listener for user data changes
        const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setUserData(data);
                setError('');
            } else {
                setUserData(null);
                setError('No user data found.');
            }
            setLoading(false);
        }, (err) => {
            setError('Failed to fetch user data.');
            setLoading(false);
        });

        // Cleanup function to unsubscribe when component unmounts or user changes
        return () => {
            unsubscribe();
        };
    }, [isOpen, currentUser]);

    useEffect(() => {
        if (!isOpen) {
            setShowTopScrollHint(false);
            setShowBottomScrollHint(false);
            return;
        }

        const timeout = setTimeout(updateScrollIndicators, 0);
        window.addEventListener('resize', updateScrollIndicators);

        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', updateScrollIndicators);
        };
    }, [isOpen, userData]);

    const logout = () => {
        onRequestClose(); // Close modal after logging out
        handleLogOut();
    };

    // Check if the current user is a guest
    const isGuestUser = currentUser && currentUser.email.includes('guest');
    const userName = isGuestUser ? t('account.guest') : userData?.userName;
    
    // Determine current plan - prioritize active plans over cancelled subscriptions
    const getCurrentPlan = () => {
        if (isGuestUser) return t('account.guestPlan');
        if (!userData?.planType) return null;
        
        // If subscription is cancelled and user has extra projects, show "My Project"
        const isSubscriptionCancelled = userData?.subscriptionStatus === 'cancel_at_period_end' || userData?.willCancelOn;
        const hasExtraProjects = userData?.extraProjectPurchased > 0;
        
        if (isSubscriptionCancelled && hasExtraProjects) {
            return t('My Project');
        }
        
        return t(userData.planType);
    };
    
    const userPlan = getCurrentPlan();
    const hasPaidPlanAccess = Boolean(
        !isGuestUser && (
            userData?.subscriptionStatus === 'active' ||
            userData?.subscriptionStatus === 'cancel_at_period_end' ||
            (userData?.extraProjectPurchased || 0) > 0 ||
            (userData?.planName && userData.planName !== 'Free') ||
            (userData?.planType && userData.planType !== 'Free')
        )
    );

    // Determine if user has a recurring subscription (vs one-time purchase)
    const isRecurringSubscription = userData?.subscriptionId && 
                                   userData?.billingCycle !== 'one-time' && 
                                   userData?.billingCycle !== 'one_time';

    // Check if subscription is scheduled for cancellation
    const isScheduledForCancellation = userData?.subscriptionStatus === 'cancel_at_period_end';
    const canManageSubscription = Boolean(userData?.stripeCustomerId);
    const canCancelSubscription = Boolean(userData?.subscriptionId && currentUser?.uid);

    // Handle subscription cancellation
    const handleCancelSubscription = async () => {
        if (!userData?.subscriptionId || !currentUser?.uid) {
            toast.error(t('account.cancellationError'));
            return;
        }

        if (!window.confirm(t('account.cancelSubscriptionConfirm'))) {
            return;
        }

        setIsProcessingCancellation(true);

        try {
            const result = await cancelSubscription(userData.subscriptionId, currentUser.uid);
            
            if (result.success) {
                const cancelDate = new Date(result.cancelDate).toLocaleDateString();
                toast.success(t('account.cancellationSuccess', { date: cancelDate }));
                
                // The real-time listener will automatically update the UI
                // No need to manually update state as Firebase will reflect the changes
            } else {
                toast.error(t('account.cancellationError'));
            }
        } catch (error) {
            console.error('Error canceling subscription:', error);
            toast.error(t('account.cancellationError'));
        } finally {
            setIsProcessingCancellation(false);
        }
    };

    // Handle opening Stripe billing portal
    const handleManageSubscription = async () => {
        if (!userData?.stripeCustomerId) {
            toast.error('No customer ID found. Please contact support.');
            return;
        }

        try {
            await createCustomerPortalSession(userData.stripeCustomerId);
        } catch (error) {
            console.error('Error opening billing portal:', error);
            toast.error('Failed to open billing portal. Please try again.');
        }
    };

    const handleBuyMoreCredits = async () => {
        if (!currentUser?.uid) {
            toast.error(t('pricing.loginRequired', { defaultValue: 'Please log in to continue.' }));
            return;
        }

        setIsBuyingCredits(true);
        try {
            await createCheckoutSession(
                EXTRA_GENERATION_CREDITS_PRICE_ID,
                currentUser.uid,
                'extra_generation_credits_pack',
                (navigator.language || 'en').startsWith('fr') ? 'fr' : 'en',
                'one-time',
                currentUser.email,
                userData?.userName || currentUser.displayName || '',
                {
                    purchaseType: 'extra_credits',
                    creditsAmount: EXTRA_GENERATION_CREDITS_PER_PURCHASE,
                    productId: EXTRA_GENERATION_CREDITS_PRODUCT_ID,
                }
            );
        } catch (purchaseError) {
            console.error('Error purchasing extra credits:', purchaseError);
            toast.error(t('account.buyCreditsError', { defaultValue: 'Unable to start checkout for additional credits.' }));
            setIsBuyingCredits(false);
        }
    };

    const handleViewPlans = () => {
        const normalizedLanguage = i18n.language || 'en';
        let localizedPricingHashPath = '/app/en#pricing';

        if (normalizedLanguage === 'fr-CA') localizedPricingHashPath = '/app/fr-CA#pricing';
        else if (normalizedLanguage === 'fr') localizedPricingHashPath = '/app/fr#pricing';
        else if (normalizedLanguage === 'en-US') localizedPricingHashPath = '/app/en-US#pricing';

        onRequestClose();

        setTimeout(() => {
            window.location.assign(localizedPricingHashPath);
        }, 100);
    };

    const handleRestartOnboarding = () => {
        const flow = isGuestUser
            ? 'guest'
            : hasPaidPlanAccess
                ? (projectCount > 0 ? 'member' : 'memberNewProject')
                : 'signup';
        const startStep = flow === 'signup'
            ? 'purchase-plan'
            : flow === 'memberNewProject'
                ? 'create-project'
                : 'project-selection';

        onRequestClose();

        setTimeout(() => {
            restartOnboarding(flow, startStep);
        }, 100);
    };

    // Format subscription information
    const formatDate = (timestamp) => {
        if (!timestamp) return null;
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    };

    const getSubscriptionStatus = () => {
        if (isGuestUser) return t('account.guestStatus');
        
        // If subscription is cancelled but user has active "My Project" purchase
        const isSubscriptionCancelled = userData?.subscriptionStatus === 'cancel_at_period_end' || userData?.willCancelOn;
        const hasExtraProjects = userData?.extraProjectPurchased > 0;
        
        if (isSubscriptionCancelled && hasExtraProjects) {
            // Show status for active "My Project" purchase
            const expiryDate = userData.subscriptionExpiryDate || userData.expiryDate;
            if (expiryDate) {
                const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
                const now = new Date();
                
                if (expiry > now) {
                    return t('account.activeUntil', { date: formatDate(expiryDate) });
                } else {
                    return t('account.expired');
                }
            }
            return t('account.active');
        }
        
        if (!userData?.subscriptionStatus) return t('account.noSubscription');
        
        const status = userData.subscriptionStatus;
        const expiryDate = userData.subscriptionExpiryDate || userData.expiryDate; // Try both field names
        const willCancelOn = userData.willCancelOn;
        
        // Handle scheduled cancellations
        if (isScheduledForCancellation && willCancelOn) {
            return t('account.subscriptionWillCancel', { date: formatDate(willCancelOn) });
        }
        
        if (status === 'active' && expiryDate) {
            const expiry = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
            const now = new Date();
            
            if (expiry > now) {
                return t('account.activeUntil', { date: formatDate(expiryDate) });
            } else {
                return t('account.expired');
            }
        }
        
        return status === 'active' ? t('account.active') : t('account.inactive');
    };

    if (loading) {
        return (
            <Modal show={isOpen} onHide={onRequestClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('account.account')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">{t('common.loading')}</span>
                        </div>
                        <p className="mt-2">{t('common.loading')}</p>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    if (error) {
        return (
            <Modal show={isOpen} onHide={onRequestClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('account.account')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        <strong>Error:</strong> {error}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={onRequestClose} className="button btn-sm">{t('common.close')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <>
            <Modal
                show={isOpen}
                onHide={onRequestClose}
                size="xl"
                centered
                dialogClassName="account-modal-dialog"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('account.account')}</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    ref={accountModalBodyRef}
                    onScroll={updateScrollIndicators}
                    className={`account-modal-body ${showTopScrollHint ? 'show-top-scroll-hint' : ''} ${showBottomScrollHint ? 'show-bottom-scroll-hint' : ''}`}
                >
            {showBottomScrollHint && (
                <div className="account-scroll-hint">
                    {t('common.scrollForMore', { defaultValue: 'Scroll to see more' })}
                </div>
            )}
            {userData && (
                <div className="account-modal-layout">
                    {/* User Information */}
                    <div className="user-info-section account-card">
                        <h4>{t('account.userInfo', { defaultValue: 'Account Details' })}</h4>
                        <div><strong>{t('account.userName')}:</strong> {userName}</div>
                        {!isGuestUser && (
                            <div><strong>{t('account.email')}:</strong> {currentUser?.email}</div>
                        )}
                        {userData.companyName && !isGuestUser && (
                            <div><strong>{t('account.companyName')}:</strong> {userData.companyName}</div>
                        )}
                    </div>

                    {/* Subscription Information */}
                    {!isGuestUser && (
                        <div className="subscription-info-section account-card">
                            <h4>{t('account.subscriptionInfo', { defaultValue: 'Plan & Billing' })}</h4>
                            <div><strong>{t('account.currentPlan')}:</strong> {userPlan || t('account.freePlan')}</div>
                            {/* Show appropriate price - My Project has a different price */}
                            {(() => {
                                const isSubscriptionCancelled = userData?.subscriptionStatus === 'cancel_at_period_end' || userData?.willCancelOn;
                                const hasExtraProjects = userData?.extraProjectPurchased > 0;
                                
                                if (isSubscriptionCancelled && hasExtraProjects) {
                                    // Show My Project price (typically $2.99)
                                    return (
                                        <div><strong>{t('account.planPrice')}:</strong> $2.99</div>
                                    );
                                } else if (userData?.planName) {
                                    // Get the correct price from pricing plans based on plan and billing cycle
                                    const plan = pricingPlans.find(p => p.name === userData.planName);
                                    const billingCycle = userData?.billingCycle || 'monthly';
                                    
                                    if (plan) {
                                        // Use the correct price based on billing cycle
                                        const correctPrice = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
                                        const displayPrice = `$${correctPrice.toFixed(2)}`;
                                        
                                        return (
                                            <div><strong>{t('account.planPrice')}:</strong> {displayPrice}</div>
                                        );
                                    } else if (userData?.planPrice) {
                                        // Fallback to stored price if plan not found
                                        return (
                                            <div><strong>{t('account.planPrice')}:</strong> {userData.planPrice}</div>
                                        );
                                    }
                                }
                                return null;
                            })()}
                            <div><strong>{t('account.status')}:</strong> {getSubscriptionStatus()}</div>
                            {/* Show appropriate billing cycle */}
                            {(() => {
                                const isSubscriptionCancelled = userData?.subscriptionStatus === 'cancel_at_period_end' || userData?.willCancelOn;
                                const hasExtraProjects = userData?.extraProjectPurchased > 0;
                                
                                if (isSubscriptionCancelled && hasExtraProjects) {
                                    // Show one-time for My Project
                                    return (
                                        <div><strong>{t('account.billingCycle')}:</strong> {t('account.oneTime')}</div>
                                    );
                                } else if (userData?.billingCycle) {
                                    return (
                                        <div><strong>{t('account.billingCycle')}:</strong> {
                                            userData.billingCycle === 'one-time' || userData.billingCycle === 'one_time'
                                                ? t('account.oneTime') 
                                                : t(`account.${userData.billingCycle}`)
                                        }</div>
                                    );
                                }
                                return null;
                            })()}
                            
                            {/* Subscription Type Display */}
                            <div>
                                <strong>{t('account.type')}:</strong> {
                                    (() => {
                                        const isSubscriptionCancelled = userData?.subscriptionStatus === 'cancel_at_period_end' || userData?.willCancelOn;
                                        const hasExtraProjects = userData?.extraProjectPurchased > 0;
                                        
                                        if (isSubscriptionCancelled && hasExtraProjects) {
                                            // Show one-time purchase for My Project
                                            return t('account.subscriptionType.oneTime');
                                        } else if (isScheduledForCancellation) {
                                            return t('account.subscriptionType.cancelled');
                                        } else if (isRecurringSubscription) {
                                            return t('account.subscriptionType.recurring');
                                        } else {
                                            return t('account.subscriptionType.oneTime');
                                        }
                                    })()
                                }
                            </div>
                            
                            {userData?.subscriptionStartDate && (
                                <div><strong>{t('account.memberSince')}:</strong> {formatDate(userData.subscriptionStartDate)}</div>
                            )}
                            
                            {/* Always show Extra Projects count */}
                            <div><strong>{t('account.extraProjects')}:</strong> {userData?.extraProjectPurchased || 0}</div>
                            <div><strong>{t('account.generationCreditsLeft', { defaultValue: 'Generation credits left' })}:</strong> {userData?.generationCreditsBalance || 0}</div>

                            <div className="mt-2">
                                {hasPaidPlanAccess ? (
                                    <>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleBuyMoreCredits}
                                            disabled={isBuyingCredits}
                                        >
                                            {isBuyingCredits
                                                ? t('common.loading', { defaultValue: 'Loading...' })
                                                : t('account.buyMoreCredits', { defaultValue: 'Buy more credits' })}
                                        </Button>
                                        <div className="mt-1">
                                            <small>{t('account.buyMoreCreditsHint', { defaultValue: `Adds ${EXTRA_GENERATION_CREDITS_PER_PURCHASE} generation credits to your balance.` })}</small>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleViewPlans}
                                        >
                                            {t('account.purchasePackage', { defaultValue: 'Purchase a package' })}
                                        </Button>
                                        <div className="mt-1">
                                            <small>{t('account.purchasePackageHint', { defaultValue: 'Choose a package before purchasing additional generation credits.' })}</small>
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            {/* Subscription Management Buttons - Only for recurring subscriptions */}
                            {isRecurringSubscription && (
                                <div className="subscription-actions mt-3">
                                    {!isScheduledForCancellation ? (
                                        <>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                className="me-2 mb-2"
                                                onClick={handleManageSubscription}
                                                disabled={!canManageSubscription || isProcessingCancellation}
                                            >
                                                {t('account.billingPortal')}
                                            </Button>
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm"
                                                className="mb-2"
                                                onClick={handleCancelSubscription}
                                                disabled={!canCancelSubscription || isProcessingCancellation}
                                            >
                                                {isProcessingCancellation ? t('common.loading') : t('account.cancelSubscription')}
                                            </Button>
                                            {!canManageSubscription && (
                                                <div className="mt-2">
                                                    <small>
                                                        {t('account.manageFromPricing', {
                                                            defaultValue: 'You can manage your subscription from the pricing page.'
                                                        })}
                                                    </small>
                                                </div>
                                            )}
                                            <div className="mt-2">
                                                <small>
                                                    <Link to="/pricing" onClick={onRequestClose}>
                                                        {t('account.manageFromPricing', {
                                                            defaultValue: 'You can also manage or cancel your subscription from the pricing page.'
                                                        })}
                                                    </Link>
                                                </small>
                                            </div>
                                        </>
                                    ) : (
                                        <Alert variant="warning" className="mt-2">
                                            <small>
                                                {t('account.subscriptionWillCancel', { 
                                                    date: formatDate(userData.willCancelOn) 
                                                })}
                                            </small>
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="links-section account-card">
                        <h4>{t('account.quickActions', { defaultValue: 'Quick Actions & Links' })}</h4>
                        <div className="account-links-row">
                            <Link to="/cookies" className="link">{t("consent.betaConsent")}</Link>
                            <Link to="/terms" className="link">{t("consent.betaTerms")}</Link>
                            <button
                                className="link account-link-button"
                                onClick={handleViewPlans}
                            >
                                {t("account.viewPlans")}
                            </button>
                            <button
                                className="link account-link-button"
                                onClick={handleRestartOnboarding}
                            >
                                {t("account.restartOnboarding")}
                            </button>
                        </div>
                        <div className="mt-2">
                            <small>{t('account.restartOnboardingHint', { defaultValue: 'Replay the guided tour for your current account state.' })}</small>
                        </div>

                        <div className="account-action-buttons">
                            {userRole === 'mainAdmin' && (
                                <Button
                                    onClick={() => setShowAdminDashboard(true)}
                                    variant="warning"
                                    className="button btn-sm me-2"
                                >
                                    🛠️ {t('Admin Control Panel')}
                                </Button>
                            )}
                            <Button onClick={logout} className="button btn-sm">{t('Logout')}</Button>
                        </div>
                    </div>
                </div>
            )}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onRequestClose} className="button btn-sm">{t('Close')}</Button>
            </Modal.Footer>
        </Modal>
        
        {/* Admin Dashboard Modal */}
        <AdminDashboardModal 
            isOpen={showAdminDashboard}
            onRequestClose={() => setShowAdminDashboard(false)}
        />
        </>
    );
};

export default AccountModal;
